import { useState, useCallback, useEffect } from 'react';
import {
  getMyFavorites,
  toggleFavorite as toggleFavoriteApi,
  removeFavorite as removeFavoriteApi,
  type FavoriteItemDto,
  type InteractionTargetType,
} from '@/api/interaction';
import { useAuth } from '@/hooks/useAuth';

const FAVORITES_UPDATED_EVENT = 'wanxiang_favorites_updated';

function normalizeItems(items: FavoriteItemDto[] | undefined): CollectItem[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (!item || (item.type !== 'solution' && item.type !== 'tool')) return null;
      const id = String(item.id || '').trim();
      if (!id) return null;
      return {
        id,
        type: item.type,
        addedAt: item.addedAt ?? null,
      };
    })
    .filter((item): item is CollectItem => Boolean(item));
}

function emitFavoritesUpdated() {
  window.dispatchEvent(new CustomEvent(FAVORITES_UPDATED_EVENT));
}

export type CollectType = InteractionTargetType;

export interface CollectItem {
  id: string;
  type: CollectType;
  addedAt: string | null;
}

export function useCollect(options?: { lazy?: boolean }) {
  const { user, isLoggedIn } = useAuth();
  const [items, setItems] = useState<CollectItem[]>([]);
  const lazy = options?.lazy ?? false;

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setItems([]);
      return;
    }
    try {
      const res = await getMyFavorites();
      setItems(normalizeItems(res.items));
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
      setItems([]);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    if (!lazy) {
      void refresh();
    }
  }, [refresh, lazy]);

  useEffect(() => {
    const handleFavoritesUpdated = () => {
      void refresh();
    };
    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    };
  }, [refresh]);

  const isCollected = useCallback(
    (id: string, type: CollectType) => {
      const safeId = String(id);
      return items.some((item) => item.id === safeId && item.type === type);
    },
    [items],
  );

  const toggle = useCallback(
    async (id: string, type: CollectType) => {
      if (!isLoggedIn || !user?.id) {
        return { success: false, message: '请先登录', isCollected: false };
      }
      try {
        const targetId = Number(id);
        if (!Number.isInteger(targetId) || targetId <= 0) {
          return { success: false, message: '目标 ID 不合法', isCollected: false };
        }
        const res = await toggleFavoriteApi({ targetType: type, targetId });
        const nextItem: CollectItem = {
          id: String(targetId),
          type,
          addedAt: null,
        };
        setItems((prev) => {
          if (res.isCollected) {
            if (prev.some((item) => item.id === nextItem.id && item.type === type)) return prev;
            return [nextItem, ...prev];
          }
          return prev.filter((item) => !(item.id === nextItem.id && item.type === type));
        });
        emitFavoritesUpdated();
        return {
          success: true,
          message: res.isCollected ? '已添加到收藏' : '已取消收藏',
          isCollected: res.isCollected,
        };
      } catch (err) {
        console.error('Failed to toggle favorite:', err);
        const message = err instanceof Error ? err.message : '收藏操作失败，请稍后重试';
        return { success: false, message, isCollected: false };
      }
    },
    [isLoggedIn, user?.id],
  );

  const remove = useCallback(
    async (id: string, type: CollectType) => {
      if (!isLoggedIn || !user?.id) {
        return { success: false, message: '请先登录' };
      }
      try {
        const targetId = Number(id);
        if (!Number.isInteger(targetId) || targetId <= 0) {
          return { success: false, message: '目标 ID 不合法' };
        }
        await removeFavoriteApi({ targetType: type, targetId });
        setItems((prev) => prev.filter((item) => !(item.id === String(targetId) && item.type === type)));
        emitFavoritesUpdated();
        return { success: true, message: '已取消收藏' };
      } catch (err) {
        console.error('Failed to remove favorite:', err);
        const message = err instanceof Error ? err.message : '取消收藏失败，请稍后重试';
        return { success: false, message };
      }
    },
    [isLoggedIn, user?.id],
  );

  const solutionIds = items.filter((item) => item.type === 'solution').map((item) => item.id);
  const toolIds = items.filter((item) => item.type === 'tool').map((item) => item.id);

  return {
    items,
    isCollected,
    toggle,
    remove,
    refresh,
    solutionIds,
    toolIds,
  };
}
